'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { InternalNetworkCredentialDto } from '@terra/shared-contracts-internal-network';
import { toasterApi } from '../../toast/ToastProvider';
import type { InternalNetworkCredentialOwnedToggle } from './InternalNetworkCredentialCard';
import {
  canVerifyInternalNetworkCredential,
  type InternalNetworkCredentialSaveBody,
  type InternalNetworkCredentialType,
  type InternalNetworkCredentialVerificationResult,
} from './internal-credentials.model';
import { useInternalNetworkCredentialVerification } from './useInternalNetworkCredentialVerification';

export type InternalNetworkCredentialsManagerCallbacks = {
  onCreate: (
    body: InternalNetworkCredentialSaveBody,
  ) => Promise<InternalNetworkCredentialDto>;
  onUpdate: (args: {
    credentialId: string;
    body: InternalNetworkCredentialSaveBody;
  }) => Promise<InternalNetworkCredentialDto>;
  onDelete: (credentialId: string) => Promise<void>;
  onVerify: (
    credentialId: string,
  ) => Promise<InternalNetworkCredentialVerificationResult>;
  onSetOwned: (args: {
    credential: InternalNetworkCredentialDto;
    isOwned: boolean;
  }) => Promise<void>;
};

const addId = ({ ids, id }: { ids: Set<string>; id: string }) =>
  new Set(ids).add(id);

const removeId = ({ ids, id }: { ids: Set<string>; id: string }) =>
  new Set([...ids].filter((existingId) => existingId !== id));

export const useInternalNetworkCredentialsManager = ({
  credentials,
  canVerify,
  verifyBlockReasonLabel,
  onCreate,
  onUpdate,
  onDelete,
  onVerify,
  onSetOwned,
}: InternalNetworkCredentialsManagerCallbacks & {
  credentials: InternalNetworkCredentialDto[];
  canVerify: boolean;
  verifyBlockReasonLabel: string | null;
}) => {
  const t = useTranslations('designSystem.networkSettings.internalCredentials');
  const verification = useInternalNetworkCredentialVerification({
    canVerify,
    verifyBlockReasonLabel,
    onVerify,
  });
  const { testCredential, clearVerificationState } = verification;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [createCredentialType, setCreateCredentialType] =
    useState<InternalNetworkCredentialType>('DOMAIN_USER');
  const [editingCredential, setEditingCredential] =
    useState<InternalNetworkCredentialDto | null>(null);
  const [deletingCredential, setDeletingCredential] =
    useState<InternalNetworkCredentialDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [ownedToggleCredentialIds, setOwnedToggleCredentialIds] = useState<
    Set<string>
  >(() => new Set());

  const openCreateDialog = (
    credentialType: InternalNetworkCredentialType = 'DOMAIN_USER',
  ) => {
    setCreateCredentialType(credentialType);
    setEditingCredential(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (credential: InternalNetworkCredentialDto) => {
    setEditingCredential(credential);
    setIsDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingCredential(null);
    }
  };

  const handleSave = async (body: InternalNetworkCredentialSaveBody) => {
    const editingCredentialId = editingCredential?.id;

    try {
      const saved = editingCredentialId
        ? await onUpdate({ credentialId: editingCredentialId, body })
        : await onCreate(body);

      toasterApi.success({ title: t('saveSuccess') });
      handleDialogOpenChange(false);

      const shouldAutoVerify =
        !editingCredentialId &&
        !!saved.id &&
        canVerifyInternalNetworkCredential({
          canVerify,
          credentialType: saved.credentialType,
        });

      if (shouldAutoVerify) {
        void testCredential({
          credentialId: saved.id,
          credentialType: saved.credentialType,
        });
      }
    } catch (error: unknown) {
      console.error(
        new Error('Failed to save internal network credential'),
        error,
      );
      toasterApi.error({ title: t('saveError') });
    }
  };

  const handleToggleOwned = useCallback(
    async ({ credentialId, isOwned }: InternalNetworkCredentialOwnedToggle) => {
      const credential = credentials.find((item) => item.id === credentialId);

      if (!credential) {
        return;
      }

      setOwnedToggleCredentialIds((previous) =>
        addId({ ids: previous, id: credentialId }),
      );

      try {
        await onSetOwned({ credential, isOwned });
        toasterApi.success({
          title: isOwned ? t('markOwnedSuccess') : t('removeOwnedSuccess'),
        });
      } catch (error: unknown) {
        console.error(
          new Error(
            'Failed to update internal network credential owned status',
          ),
          error,
        );
        toasterApi.error({
          title: isOwned ? t('markOwnedError') : t('removeOwnedError'),
        });
      } finally {
        setOwnedToggleCredentialIds((previous) =>
          removeId({ ids: previous, id: credentialId }),
        );
      }
    },
    [credentials, onSetOwned, t],
  );

  const handleConfirmDelete = async () => {
    if (!deletingCredential) {
      return;
    }

    setIsDeleting(true);

    try {
      await onDelete(deletingCredential.id);
      clearVerificationState(deletingCredential.id);
      setDeletingCredential(null);
    } catch (error: unknown) {
      console.error(
        new Error('Failed to delete internal network credential'),
        error,
      );
      toasterApi.error({ title: t('deleteError') });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    ...verification,
    isDialogOpen,
    createCredentialType,
    editingCredential,
    deletingCredential,
    isDeleting,
    ownedToggleCredentialIds,
    openCreateDialog,
    openEditDialog,
    handleDialogOpenChange,
    handleSave,
    handleToggleOwned,
    setDeletingCredential,
    handleConfirmDelete,
  };
};
